/**
 * 送れていない録音の帯と、自動の送り直し（PRODUCT_DEV-1270）
 *
 * 端末に入れるのはフラグ offlineRecordingSave が ON のときだけだが、帯と送り直しはフラグを見ない。
 * あとからフラグを OFF にしても、残っている録音を送れる・消せるようにするため（持ち主が片付けられなくなる）。
 *
 * 画面全体（router のシェル）に1つだけ置く。開いたとき・電波が戻ったとき（online）・画面に戻ったとき・
 * 残りがある間は1分ごとに、この人の送れていない録音を送り直す。
 * 録音中の画面では帯を出さず、送り直しもしない（録音・対話の通信を邪魔しないため）。
 * 送り直しても通らないもの（failed）は自動では送らず、「今すぐ送る」か「消す」を人に選ばせる。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { CloudOff, Loader2 } from "lucide-react";
import { useRootLoader } from "@/hooks/useRootLoader";
import {
  ROUTE_MEMO_RECORDING,
  ROUTE_MINUTES_RECORDING,
  ROUTE_RECORDING,
  ROUTE_SPEED_RECORDING,
} from "@/utils/const";
import {
  discardPendingRecording,
  DRAFT_STALE_MS,
  isStaleDraft,
  listMyPending,
  promoteDraft,
  PENDING_CHANGED_EVENT,
  sendAllPending,
} from "@/lib/offlineRecording/pendingRecordings";
import type { PendingRecording } from "@/lib/offlineRecording/types";

const RETRY_INTERVAL_MS = 60_000;
const RECORDING_PATHS = [ROUTE_RECORDING, ROUTE_SPEED_RECORDING, ROUTE_MEMO_RECORDING, ROUTE_MINUTES_RECORDING];

export const isRecordingPath = (pathname: string): boolean =>
  RECORDING_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

export const PendingRecordingsBanner: React.FC = () => {
  const { auth } = useRootLoader();
  const { pathname } = useLocation();
  const recording = isRecordingPath(pathname);
  const tenantId = auth.currentTenantId || "";
  const userId = auth.user.id;
  const [items, setItems] = useState<PendingRecording[]>([]);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!tenantId || !userId) return setItems([]);
    setItems(await listMyPending(tenantId, userId));
  }, [tenantId, userId]);

  const send = useCallback(
    async (includeFailed: boolean) => {
      if (!tenantId || !userId || sendingRef.current) return;
      sendingRef.current = true;
      setSending(true);
      try {
        await sendAllPending(tenantId, userId, { includeFailed });
      } finally {
        sendingRef.current = false;
        setSending(false);
        void refresh();
      }
    },
    [tenantId, userId, refresh],
  );

  // 件数の変化（録音画面が入れた・送れた）を拾う
  useEffect(() => {
    void refresh();
    const onChanged = () => void refresh();
    window.addEventListener(PENDING_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(PENDING_CHANGED_EVENT, onChanged);
  }, [refresh]);

  // 新しい控え（録音画面が書いている・落ちたばかり）がある間は、古くなったころに数え直す
  // （落ちてすぐ開き直したとき、時間がたっても「途中で終わった」が出ないままにしない）
  const hasFreshDraft = items.some((i) => i.draft && !isStaleDraft(i, Date.now()));
  useEffect(() => {
    if (!hasFreshDraft) return;
    const timer = window.setTimeout(() => void refresh(), DRAFT_STALE_MS + 1000);
    return () => window.clearTimeout(timer);
  }, [hasFreshDraft, items, refresh]);

  // 自動の送り直し（録音中の画面ではしない）
  const autoCount = items.filter((i) => !i.failed && !i.draft).length;
  useEffect(() => {
    if (recording) return;
    const trySend = () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      void send(false);
    };
    trySend();
    const onVisible = () => {
      if (document.visibilityState === "visible") trySend();
    };
    window.addEventListener("online", trySend);
    document.addEventListener("visibilitychange", onVisible);
    const timer = autoCount > 0 ? window.setInterval(trySend, RETRY_INTERVAL_MS) : undefined;
    return () => {
      window.removeEventListener("online", trySend);
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) window.clearInterval(timer);
    };
  }, [recording, autoCount, send]);

  // 対話の控え: 録音画面が書いている最中のもの（新しい）は出さない。書かれなくなったもの（アプリが落ちた）だけ出す
  const now = Date.now();
  const staleDrafts = items.filter((i) => isStaleDraft(i, now));
  const queued = items.filter((i) => !i.draft);
  if (recording || (queued.length === 0 && staleDrafts.length === 0)) return null;
  const failed = queued.filter((i) => i.failed);

  return (
    <div className="mx-4 mt-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 shadow-card">
      {queued.length > 0 && (
        <div className="flex items-center gap-2">
          <CloudOff size={16} className="flex-none text-neutral-500" />
          <p className="m-0 flex-1 text-xs text-neutral-700">
            送れていない録音が{countRecordings(queued)}件あります。
            {failed.length === 0 ? "つながったら自動で送ります。" : "送り直すか、消してください。"}
          </p>
          <button
            onClick={() => void send(true)}
            disabled={sending}
            className="flex flex-none items-center gap-0.5 rounded-full border border-primary bg-white px-2 py-0.5 text-xs font-bold text-primary disabled:opacity-40"
          >
            {sending && <Loader2 size={12} className="animate-spin" />}
            今すぐ送る
          </button>
        </div>
      )}
      {failed.length > 0 && (
        <ul className="m-0 mt-2 list-none space-y-1 p-0">
          {failed.map((f) => (
            <PendingRow key={f.id} item={f} />
          ))}
        </ul>
      )}
      {staleDrafts.length > 0 && (
        <div className={queued.length > 0 ? "mt-2 border-t border-neutral-200 pt-2" : ""}>
          <p className="m-0 mb-1 text-xs text-neutral-700">
            途中で終わった対話の記録があります。ここまでの会話で記録を作れます。
          </p>
          <ul className="m-0 list-none space-y-1 p-0">
            {staleDrafts.map((d) => (
              <PendingRow
                key={d.id}
                item={d}
                onSave={() =>
                  void promoteDraft(d.id).then(() => {
                    if (navigator.onLine !== false) void send(false);
                  })
                }
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const KIND_LABEL: Record<PendingRecording["kind"], string> = {
  speed: "自由記録",
  memo: "音声メモ",
  dialogue: "対話の記録",
  minutesChunk: "議事録の録音",
  minutesFinalize: "議事録の確定",
};

/** 1件の行（いつの何か・「そこまでで保存」・「消す」） */
const PendingRow: React.FC<{ item: PendingRecording; onSave?: () => void }> = ({ item, onSave }) => (
  <li className="flex items-center gap-2 text-xs text-neutral-600">
    <span className="flex-1">
      {KIND_LABEL[item.kind]}（
      {new Date(item.createdAt).toLocaleString("ja-JP", {
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}
      ）
    </span>
    {onSave && (
      <button onClick={onSave} className="flex-none text-xs font-bold text-primary">
        そこまでで保存
      </button>
    )}
    <button
      onClick={() => {
        if (window.confirm("この記録を端末から消します。元に戻せません。よろしいですか？")) {
          void discardPendingRecording(item.id);
        }
      }}
      className="flex-none text-xs font-bold text-danger"
    >
      消す
    </button>
  </li>
);

/** 件数は記録ごと（議事録の区切りは、同じ議事録のものを1件と数える） */
const countRecordings = (items: PendingRecording[]): number =>
  new Set(
    items.map((i) =>
      i.kind === "minutesChunk" || i.kind === "minutesFinalize"
        ? `minutes:${String(i.request.minutesId)}`
        : i.id,
    ),
  ).size;
